import { useCallback, useContext, useMemo, useState } from 'react'

import { useMutation } from '@tanstack/react-query'
import { Alert } from '@vtex/shoreline'
import { t } from 'i18next'
import moment from 'moment'

import { Button } from '~/components/Buttons'
import { FeedbackContext } from '~/components/feedback'
import Form from '~/components/unform/Form'
import NameAndTimeInterval from '~/components/unform/FormGroup/NameAndTimeInterval'
import { handleValidationsErrorsAPI, history } from '~/helpers'
import { useAppInfo } from '~/hooks/useAppInfo'
import { useErrorApiAlert } from '~/hooks/useErrorApiAlert'
import useQueryParams from '~/hooks/useQuery'
import AdvancedSettings from '~/modules/retailMedia/components/CampaignForm/elements/Advanced'
import { DigitalSignageConfirmationModal } from '~/modules/retailMedia/components/CampaignForm/elements/DigitalSignageConfirmationModal'
import Investment from '~/modules/retailMedia/components/CampaignForm/elements/Investment'
import { Video } from '~/modules/retailMedia/components/CampaignForm/elements/Video'
import { useSubmitErrorMessage } from '~/modules/retailMedia/components/CampaignForm/hooks/useSubmitErrorMessage'
import {
  createCampaignKey,
  createCampaignService
} from '~/modules/retailMedia/services/campaign'

import CampaignType from '../../../../elements/CampaignType'
import Screens from '../../../../elements/Screens'
import Subpublishers from '../../../../elements/Subpublishers'
import { applyPreparedCampaignBody } from '../../../../helpers/applyPreparedCampaignBody'
import { useCampaignForm } from '../../../../hooks/useCampaignForm'
import { usePrepareCampaignCreateBody } from '../../../../hooks/usePrepareCampaignCreateBody'
import { withDefaultAgeRestrictedSetting } from '../../../helpers'
import { DTOCreateDigitalSignageCampaign } from './dto'

const DigitalSignage: React.FC = () => {
  const { toast } = useContext(FeedbackContext)

  const {
    formRef,
    defaultCampaignName,
    handleDebugSubmit,
    toggleDebugMode,
    campaignPermissions
  } = useCampaignForm()
  const {
    prepareCampaignCreateBody,
    validateLinkedInsertionOrder,
    isLinkedInsertionOrderReady
  } = usePrepareCampaignCreateBody()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [hasFileUploadError, setHasFileUploadError] = useState(false)
  const [requiredData, setRequiredData] = useState<RequiredDataToSubmit>({
    isMediaValid: false,
    isInvestmentValid: false
  })

  const [bodyRequest, setBodyRequest] = useState(
    {} as DTOCreateDigitalSignageCampaignResponse
  )

  const rmid = useQueryParams().get('rmid')

  const { isTotalBudgetEditable, costType } = useMemo(() => {
    const costType =
      (campaignPermissions?.cost_type as CampaignPermissionsCostType) || null

    return {
      isTotalBudgetEditable: costType === 'total_budget',
      costType
    }
  }, [campaignPermissions?.cost_type])

  /**
   * Form is ready
   * Valor para controlarmos se devemos bloquear o submit
   */
  const formIsReadyToSubmit = useMemo(() => {
    const propsDefined =
      bodyRequest?.name && bodyRequest?.start_at && bodyRequest?.end_at

    const hasSelectedPlaces =
      bodyRequest?.settings?.places?.length > 0 ||
      !!bodyRequest?.settings?.use_all_places

    const isInvestmentValid =
      costType !== 'dynamic' ? requiredData?.isInvestmentValid : true

    return (
      !hasFileUploadError &&
      propsDefined &&
      hasSelectedPlaces &&
      isInvestmentValid &&
      requiredData.isMediaValid &&
      isLinkedInsertionOrderReady
    )
  }, [
    bodyRequest,
    hasFileUploadError,
    requiredData.isMediaValid,
    requiredData.isInvestmentValid,
    isTotalBudgetEditable,
    costType,
    isLinkedInsertionOrderReady
  ])

  /**
   * Handle request
   */
  const { submitErrorMessage, resolveSubmitError } = useSubmitErrorMessage()

  const [AlertErrorCampaign, setErrorCampaign] = useErrorApiAlert({
    title: submitErrorMessage
  })

  const { mutate, isPending } = useMutation({
    mutationKey: [createCampaignKey],
    mutationFn: createCampaignService,
    onSuccess: response => {
      history.push(`/retail-media/campaign/view/${response.data.id}`)

      toast.success(t('rm:campaign.create.toast.success.message'))
    },
    onError: error => {
      setErrorCampaign(error)

      handleValidationsErrorsAPI(error, formRef)

      toast.critical(resolveSubmitError(error))
    }
  })

  /**
   * Handle submit
   */
  const { lengthCentsCurrency } = useAppInfo()

  const handleSubmit = useCallback(
    async (formData: CreateDigitalSignageFormData) => {
      const preData = {
        ...formData,
        publisher_id: rmid,
        lengthCentsCurrency,
        costType: costType as CampaignPermissionsCostType
      }

      const body = DTOCreateDigitalSignageCampaign(preData)
      const action = handleDebugSubmit || mutate

      applyPreparedCampaignBody(body, prepareCampaignCreateBody, action)
    },
    [
      lengthCentsCurrency,
      rmid,
      handleDebugSubmit,
      mutate,
      costType,
      prepareCampaignCreateBody
    ]
  )

  /**
   * Handle form change
   */
  const handleFormChange = () => {
    const formData = formRef?.current?.getData() as
      | CreateDigitalSignageFormData
      | undefined

    return setBodyRequest(
      DTOCreateDigitalSignageCampaign({
        ...formData,
        publisher_id: rmid,
        lengthCentsCurrency,
        costType: costType as CampaignPermissionsCostType
      })
    )
  }

  /**
   * initialData
   */
  const initialData: Partial<CreateDigitalSignageFormData> = {
    type: 'digital_signage',
    name: defaultCampaignName,
    start_date: moment().format('YYYY-MM-DD'),
    settings: withDefaultAgeRestrictedSetting(),
    places: {
      placesCheck: 'all',
      place_ids: ''
    },
    subpublishers: {
      subpublishersCheck: 'all'
    }
  }

  return (
    <Form
      ref={formRef}
      onSubmit={handleSubmit}
      onChange={handleFormChange}
      initialData={initialData}
    >
      <CampaignType />
      <NameAndTimeInterval
        formRef={formRef}
        titlePanel={t('rm:campaign.create.NameAndTimeInterval.titlePanel')}
        labelTitle={t('rm:campaign.create.NameAndTimeInterval.labelTitle')}
        placeholderTitle={t(
          'rm:campaign.create.NameAndTimeInterval.placeholderTitle'
        )}
        hasDescription
        labelDescription={t(
          'rm:campaign.create.NameAndTimeInterval.labelDescription'
        )}
        placeholderDescription={t(
          'rm:campaign.create.NameAndTimeInterval.placeholderDescription'
        )}
        onlyDates
        showValidBadgeOnSection
      />

      <Subpublishers />

      <Screens formRef={formRef} handleFormChange={handleFormChange} />

      <Video
        formRef={formRef}
        onError={setHasFileUploadError}
        setRequiredData={setRequiredData}
      />

      <Investment
        formRef={formRef}
        formData={bodyRequest}
        requiredData={requiredData}
        setRequiredData={setRequiredData}
        isTotalBudgetEditable={isTotalBudgetEditable}
        formIsReadyToSubmit={formIsReadyToSubmit}
        adCostType={costType as CampaignPermissionsCostType}
      />

      <AdvancedSettings showDeliveryPace={false} />

      <AlertErrorCampaign />

      {!!handleDebugSubmit && (
        <Alert variant="critical">
          🚀 ~ DEBUG MODE ATIVADO: este modo imprime o payload da requisição no
          console e desabilita o envio do formulário.
        </Alert>
      )}

      <div className="form-default__buttons">
        {!!handleDebugSubmit && (
          <Button
            type="button"
            text={'Desativar modo debug'}
            onClick={toggleDebugMode}
            size="large"
            template="critical"
          />
        )}

        <Button
          type="button"
          text={t('rm:sendForApproval')}
          loading={isPending}
          size="large"
          template="primary"
          disabled={!formIsReadyToSubmit}
          onClick={() => {
            if (!validateLinkedInsertionOrder()) {
              return
            }

            setIsModalOpen(true)
          }}
        />
      </div>

      <DigitalSignageConfirmationModal
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        formRef={formRef}
        isPending={isPending}
        isTotalBudgetEditable={isTotalBudgetEditable}
      />
    </Form>
  )
}

export default DigitalSignage
